import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {LidarOverviewContext} from "./context/LidarOverviewContext";
import Row from "@meridian/components/row";
import {
    getAssetRefreshInterval
} from "../../util/Util";
import Column from "@amzn/meridian/column";
import {FormattedMessage, injectIntl} from "react-intl";
import {
    BLUE_COLOR, ONLINE_OK,
    ORANGE_COLOR, THREE_DOTS,
} from "../../util/Constants";
import Button from "@amzn/meridian/button";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Icon from "@amzn/meridian/icon";
import Box from "@amzn/meridian/box";
import Tooltip from "@amzn/meridian/tooltip";
import Loader from "../../loader/Loader";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import LidarControllerBanner from "../thinclient/banner/LidarControllerBanner";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import AddEditSingleLidarControllerModal from "./modal/AddEditSingleLidarControllerModal";
import Text from "@amzn/meridian/text";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import LidarConfigurationModal from "./modal/LidarConfigurationModal";

const LidarOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(LidarOverviewContext);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openConfigModal, setOpenConfigModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const tableEndRef = useRef(null);
    const actionButtonRefs = useRef([]);
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        if (!openAddModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal]);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.lidarDeviceInformation.length);
    }, [viewModel.lidarDeviceInformation]);

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    const sortedDeviceList = Object.values(viewModel.lidarDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        if (a[viewModel.sortPref.column] < b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? -1 : 1
        }
        if (a[viewModel.sortPref.column] > b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? 1 : -1
        }
        return 0
    })

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const getData = () => {
        setOpenAddModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const onClickAddEditDevice = () => {
        setSelected(null);
        setOpenAddModal(true);
    }

    const onClickEditDevice = (mac) => {
        setSelected(mac);
        setOpenAddModal(true);
    }

    const onClickViewConfiguration = (mac) => {
        setSelected(mac);
        setOpenConfigModal(true);
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom!'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert
                            type="informational"
                            size="small"
                        >
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow highlightOnHover={false}>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <TableCell sortColumn="connectivity">
                                    <FormattedMessage id="device.connectivity" defaultMessage="Connection Status"/>
                                </TableCell>
                                <TableCell sortColumn="location">
                                    <FormattedMessage id="device.location" defaultMessage="Locations"/>
                                </TableCell>
                                <TableCell sortColumn="status">
                                    <FormattedMessage id="device.status" defaultMessage="Status"/>
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            <LidarControllerBanner/>
                            {sortedDeviceList.map((device, index) => (
                                <TableRow key={device.mac}
                                          highlightOnHover={true}>
                                    <TableCell> <b>{device.mac}</b>
                                        <br></br>
                                        {device.ip}
                                    </TableCell>
                                    <TableCell> <b style={{color: device.connectivity.toLowerCase() === ONLINE_OK ? BLUE_COLOR : ORANGE_COLOR}}>{device.connectivity}</b>
                                        <br></br>
                                        {device.timestamp}
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.location}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{device.status}</b>
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300">
                                            <Button ref={el => actionButtonRefs.current[index] = el}
                                                               type="icon"
                                                               size="medium"
                                                               onClick={() => onClickActionsButton(index)}
                                                               aria-controls="my-menu"
                                                               aria-haspopup="menu"
                                                               aria-expanded="false"
                                                >
                                                    {THREE_DOTS}
                                                </Button>
                                            <Menu anchorNode={actionButtonRefs.current[index]}
                                                  open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                  position="left"
                                                  onClose={() => onCloseActions(index)}>
                                                <MenuItem onClick={() => onClickEditDevice(device.mac)}>
                                                    <FormattedMessage id="actions.addDevice"
                                                                      defaultMessage="Edit Device"/>
                                                </MenuItem>
                                                <MenuItem onClick={() => onClickViewConfiguration(device.mac)}>
                                                    <FormattedMessage id="actions.viewConfiguration"
                                                                      defaultMessage="View Configuration"/>
                                                </MenuItem>
                                            </Menu>
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>}
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <Button onClick={onClickAddEditDevice} type="primary" size="small">
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="device.add_device_button_modal" defaultMessage="Add/Update Single Device Configuration"/>
                            </Button>
                    </Row>
                    <AddEditSingleLidarControllerModal open={openAddModal} setOpen={setOpenAddModal}
                                              selected={selected} setSelected={setSelected}/>
                    <LidarConfigurationModal open={openConfigModal} setOpen={setOpenConfigModal}
                                                   selected={selected} setSelected={setSelected}/>
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(LidarOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/LidarOverview.js